/**
 * sesion.tsx — el estado del acceso, y las tres cosas que se pueden hacer con
 * él: entrar, elegir empresa, salir.
 *
 * Las reglas de qué pantalla toca viven en `flujo.ts`, que se prueba sin
 * navegador. Acá está el cableado: hablar con Supabase, hablar con nuestra
 * API, y no perder ningún estado por el camino.
 *
 * ── Lo que la interfaz NO decide ──────────────────────────────────────────
 *
 * La `capacidad` que llega en la sesión —`plena` o `piso`— sirve para que la
 * pantalla no ofrezca lo que va a ser rechazado. **No es el control.** Quien
 * decide es la base, en cada petición.
 *
 * Un botón deshabilitado se salta con una petición hecha a mano, así que si
 * algún día la interfaz y la base discrepan, la que manda es la base y la
 * interfaz está mintiendo. Ese orden importa tenerlo claro antes de escribir
 * la primera pantalla que oculte algo.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import { configurado, supabase } from './supabase.ts';
import { anioYMesDeHoy } from './hoy.ts';
import {
  empresaEfectiva, pantallaPara,
  type EmpresaDisponible, type EstadoDeAcceso, type Pantalla,
} from './flujo.ts';

const RECUERDO = 'sb-empresa';

export interface Sesion {
  readonly empresa: EmpresaDisponible;
  readonly rol: 'admin' | 'contador' | 'solo_lectura';
  readonly capacidad: 'plena' | 'piso';
  /**
   * El mes abierto en el que se trabaja: el periodo abierto más viejo sin cerrar
   * (0150). Las pantallas defaultean sus selectores a él, no al mes calendario,
   * porque mientras un mes no se cierre el trabajo sigue ahí. `null` sólo si la
   * empresa no tiene periodos; entonces el frente cae al calendario.
   */
  readonly mesDeTrabajo: { readonly anio: number; readonly mes: number } | null;
}

interface Contexto {
  readonly pantalla: Pantalla;
  readonly empresas: readonly EmpresaDisponible[];
  /**
   * Puede dar de alta clientes. NO le da acceso a los libros de nadie.
   *
   * Sirve para OFRECER la consola. Poder usarla lo decide `abrir_operacion` en
   * la base, en cada petición. Si algún día discrepan, la pantalla ofrece un
   * botón que no funciona — molesto, y no es un agujero.
   */
  readonly esOperador: boolean;
  readonly sesion: Sesion | null;
  readonly correo: string | null;
  /** El último fallo, ya traducido a algo que se puede mostrar. */
  readonly fallo: string | null;
  readonly ocupado: boolean;
  entrar(correo: string, clave: string): Promise<void>;
  elegir(empresaId: string): void;
  /** Vuelve al selector sin cerrar la sesión de identidad. */
  cambiarEmpresa(): void;
  /**
   * Vuelve a pedir la lista de empresas.
   *
   * ▶ Existe por `/configuracion`: cambiar la razón social ahí y que la barra
   * superior siguiera diciendo la anterior hasta recargar es exactamente un
   * botón que hace la mitad de lo que dice. Los datos de la empresa —nombre,
   * moneda, idioma por omisión— viajan con esta lista, así que refrescarla es
   * todo lo que hace falta.
   */
  refrescarEmpresas(): void;
  salir(): Promise<void>;
  /** Una llamada a nuestra API con el token y la empresa puestos. */
  pedir(ruta: string, init?: RequestInit): Promise<Response>;
}

const Ctx = createContext<Contexto | null>(null);

function recordada(): string | null {
  try { return localStorage.getItem(RECUERDO); } catch { return null; }
}

export function ProveedorDeSesion({ children }: { children: ReactNode }) {
  const [comprobando, setComprobando] = useState(true);
  const [token, setToken] = useState<string | null>(null);
  const [correo, setCorreo] = useState<string | null>(null);
  const [empresas, setEmpresas] = useState<readonly EmpresaDisponible[] | null>(null);
  const [tickEmpresas, setTickEmpresas] = useState(0);
  const [esOperador, setEsOperador] = useState(false);
  const [elegida, setElegida] = useState<string | null>(recordada);
  const [sesion, setSesion] = useState<Sesion | null>(null);
  const [fallo, setFallo] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  /**
   * ▶ QUÉ EMPRESA ESTÁ ACTIVA. Sube acá porque `pedir` la necesita.
   *
   * `elegida` es lo que dice el navegador; `activa` es lo que la lista de
   * empresas CONFIRMA. Cuando el id recordado no está en la lista —a alguien le
   * quitaron un acceso y le quedó la empresa guardada— las dos discrepan.
   *
   * Y discrepaban de verdad: la aplicación se montaba con `activa` y **cada
   * petición mandaba `elegida`**, así que la pantalla dibujaba una empresa y la
   * API contestaba sobre otra. Salía «no tiene acceso a esa empresa» encima de
   * un tablero que se estaba dibujando — un error que la persona no podía
   * resolver porque no correspondía a lo que estaba viendo.
   *
   * Dos representaciones del mismo hecho, y la que rige no era la que se lee.
   * Ahora la cabecera sale de `activa`: una sola decisión.
   */
  const estado: EstadoDeAcceso = useMemo(() => ({
    configurado,
    comprobando,
    autenticado: token !== null,
    empresas,
    empresaRecordada: elegida,
  }), [comprobando, token, empresas, elegida]);

  const activa = useMemo(() => empresaEfectiva(estado), [estado]);

  /* ── La sesión de Supabase ─────────────────────────────────────────────── */

  useEffect(() => {
    if (!supabase) { setComprobando(false); return; }

    let vivo = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!vivo) return;
      setToken(data.session?.access_token ?? null);
      setCorreo(data.session?.user.email ?? null);
      setComprobando(false);
    });

    // El token se renueva solo. Sin esta suscripción, la aplicación seguiría
    // usando el viejo y empezaría a recibir 401 a mitad de sesión — que se ve
    // como «se cayó» y no como «hay que renovar».
    const { data: sub } = supabase.auth.onAuthStateChange((_evento, s) => {
      setToken(s?.access_token ?? null);
      setCorreo(s?.user.email ?? null);
      if (!s) { setEmpresas(null); setSesion(null); setEsOperador(false); }
    });

    return () => { vivo = false; sub.subscription.unsubscribe(); };
  }, []);

  /* ── Nuestra API ───────────────────────────────────────────────────────── */

  const pedir = useCallback(async (ruta: string, init: RequestInit = {}) => {
    const cabeceras = new Headers(init.headers);
    if (token) cabeceras.set('authorization', `Bearer ${token}`);
    // La confirmada por la lista, no la recordada por el navegador. Ver arriba.
    if (activa) cabeceras.set('x-empresa', activa.id);
    return fetch(ruta, { ...init, headers: cabeceras });
  }, [token, activa]);

  // Las empresas se piden en cuanto hay token, y se vuelven a pedir si cambia.
  useEffect(() => {
    if (!token) { setEmpresas(null); setEsOperador(false); return; }
    let vivo = true;

    (async () => {
      try {
        const r = await fetch('/api/empresas', {
          headers: { authorization: `Bearer ${token}` },
        });
        if (!vivo) return;
        if (!r.ok) { setEmpresas([]); setFallo('no_se_pudo_listar'); return; }
        const cuerpo = await r.json() as {
          empresas: EmpresaDisponible[]; esOperador?: boolean;
        };
        setEmpresas(cuerpo.empresas);
        setEsOperador(cuerpo.esOperador === true);
      } catch {
        // Sin red o sin API. Lista vacía + fallo con nombre: la pantalla de
        // «ninguna empresa» dice que fue un fallo, no que no pertenece a
        // ninguna. Confundirlos manda a alguien a pedir una invitación que ya
        // tiene.
        if (vivo) { setEmpresas([]); setFallo('sin_conexion'); }
      }
    })();

    return () => { vivo = false; };
  }, [token, tickEmpresas]);

  /* ── Abrir la sesión en una empresa ────────────────────────────────────── */

  // Con la empresa resuelta se abre la sesión contra la API, que es lo que
  // devuelve el rol y la capacidad. No se dan por sabidos desde la lista: la
  // lista dice a qué pertenece, y `abrir_sesion` dice con qué entra.
  useEffect(() => {
    if (!token || !activa) { setSesion(null); return; }
    let vivo = true;

    (async () => {
      try {
        const r = await fetch('/api/sesion', {
          method: 'POST',
          headers: { authorization: `Bearer ${token}`, 'x-empresa': activa.id },
        });
        if (!vivo) return;
        if (!r.ok) {
          // Le quitaron la membresía entre el listado y esto, o el token
          // venció. Se olvida la empresa y se vuelve a preguntar.
          setSesion(null);
          setElegida(null);
          try { localStorage.removeItem(RECUERDO); } catch { /* sin almacenamiento */ }
          setFallo('sin_acceso');
          return;
        }
        const cuerpo = await r.json() as {
          rol: Sesion['rol']; capacidad: Sesion['capacidad'];
          mesDeTrabajo: Sesion['mesDeTrabajo'];
        };
        setSesion({
          empresa: activa, rol: cuerpo.rol, capacidad: cuerpo.capacidad,
          mesDeTrabajo: cuerpo.mesDeTrabajo ?? null,
        });
        setFallo(null);
      } catch {
        if (vivo) { setSesion(null); setFallo('sin_conexion'); }
      }
    })();

    return () => { vivo = false; };
  }, [token, activa]);

  /* ── Las tres acciones ─────────────────────────────────────────────────── */

  const entrar = useCallback(async (correoDado: string, clave: string) => {
    if (!supabase) { setFallo('sin_configurar'); return; }
    setOcupado(true);
    setFallo(null);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: correoDado.trim(), password: clave,
      });
      // Un fallo de red y unas credenciales malas NO se cuentan igual. Decir
      // «correo o contraseña incorrectos» cuando en realidad no hay red manda
      // a alguien a cambiar una contraseña que estaba bien.
      if (error) {
        setFallo(error.status === undefined ? 'sin_conexion' : 'credenciales');
      }
    } catch {
      setFallo('sin_conexion');
    } finally {
      setOcupado(false);
    }
  }, []);

  const elegir = useCallback((empresaId: string) => {
    setElegida(empresaId);
    try { localStorage.setItem(RECUERDO, empresaId); } catch { /* sin almacenamiento */ }
  }, []);

  const cambiarEmpresa = useCallback(() => {
    setElegida(null);
    setSesion(null);
    try { localStorage.removeItem(RECUERDO); } catch { /* sin almacenamiento */ }
  }, []);

  const salir = useCallback(async () => {
    try { localStorage.removeItem(RECUERDO); } catch { /* sin almacenamiento */ }
    setElegida(null);
    setSesion(null);
    setEmpresas(null);
    await supabase?.auth.signOut();
  }, []);

  const refrescarEmpresas = useCallback(() => setTickEmpresas((x) => x + 1), []);

  const valor = useMemo<Contexto>(() => ({
    pantalla: pantallaPara(estado),
    empresas: empresas ?? [],
    esOperador,
    sesion,
    correo,
    fallo,
    ocupado,
    entrar,
    elegir,
    cambiarEmpresa,
    refrescarEmpresas,
    salir,
    pedir,
  }), [estado, empresas, esOperador, sesion, correo, fallo, ocupado, entrar, elegir,
    cambiarEmpresa, refrescarEmpresas, salir, pedir]);

  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export function useSesion(): Contexto {
  const c = useContext(Ctx);
  if (!c) throw new Error('useSesion fuera del ProveedorDeSesion');
  return c;
}

/**
 * `true` si la empresa activa puede REGISTRAR.
 *
 * Con capacidad de piso lee, exporta y descarga sus periodos cerrados, y no
 * registra. Ver la cabecera de este archivo: esto decide qué se OFRECE, no qué
 * se permite.
 */
export function puedeRegistrar(sesion: Sesion | null): boolean {
  return sesion?.capacidad === 'plena' && sesion.rol !== 'solo_lectura';
}

/**
 * `true` si la empresa activa puede ADMINISTRARSE.
 *
 * ══ ▶▶ NO ES LO MISMO QUE REGISTRAR, Y SE OFRECÍA COMO SI LO FUERA ═════
 *
 * Hay tres actos que la base le exige a un ADMIN y no a quien escribe:
 *
 *     abrir un ejercicio          · 0078  · SB227
 *     cerrar un ejercicio         · 0079  · SB228
 *     declarar que no hay saldos  · 0064  · SB229
 *
 * Dos de los tres se ofrecían detrás de `puedeRegistrar`, que un **contador**
 * pasa. La caminata por rol del 13 de setiembre entró como contador, pulsó
 * «Abrir 2027», y la base lo rechazó — correctamente. Lo que estaba mal era
 * haberlo ofrecido: un botón vivo es una promesa, y éste no se podía cumplir.
 *
 * ▶ Y esto decide qué se OFRECE, no qué se permite — igual que `puedeRegistrar`,
 * y por el mismo motivo: quien permite es la base, siempre. Esconder el botón
 * no es la seguridad; es no mentirle a la persona.
 */
export function puedeAdministrar(sesion: Sesion | null): boolean {
  return sesion?.capacidad === 'plena' && sesion.rol === 'admin';
}

/**
 * El mes con el que ARRANCA un selector de periodo: el mes de trabajo de la
 * sesión (el abierto más viejo sin cerrar, 0150), o el calendario si la empresa
 * no tiene periodos.
 *
 * ▶ Viaja en la sesión y no se pide aparte, así que está listo ANTES de que la
 * pantalla monte: el selector nace en el mes correcto, sin un parpadeo del mes
 * de hoy ni una segunda carga de datos. Es para el valor INICIAL —qué mirar por
 * omisión—; una vez que la persona elige otro mes, manda su elección.
 *
 * NO se usa para la fecha de un hecho real (la compra de un activo, el inicio de
 * un proyecto): eso es una fecha del mundo, no el mes que se está mirando, y
 * sigue saliendo del calendario.
 */
export function useMesDeTrabajo(): { anio: number; mes: number } {
  const { sesion } = useSesion();
  return sesion?.mesDeTrabajo ?? anioYMesDeHoy();
}
